import React, { useState, useEffect, useRef, useMemo } from 'react';
import { TopNav, Button, Icon } from '@ab-inbev-maz/compass';
import { useAuthStore } from '../features/sso-auth/store/authStore';
import logo from '../assets/images/logo_smart_comms.svg'

const Header: React.FC = () => {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  let userInfoProfile = useAuthStore(state => state.user);
  const logout = useAuthStore(state => state.logout);
  const locales = useAuthStore(state => state.locales);
  const changeLocale = useAuthStore(state => state.changeLocale);

  // Si el usuario no está en el store, intenta obtenerlo de localStorage
  if (!userInfoProfile) {
    const localUser = localStorage.getItem('user');
    if (localUser) {
      try {
        userInfoProfile = JSON.parse(localUser);
      } catch (e) {
        userInfoProfile = null;
      }
    }
  }

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);

  const countryCode = useMemo(() => userInfoProfile?.country_code?.toLowerCase() || 'mx', [userInfoProfile]);
  const countryName = useMemo(() => userInfoProfile?.country_name || 'México', [userInfoProfile]);
  const hasMultipleLocales = Array.isArray(locales) && locales.length > 1;

  const dropdownRef = useRef<HTMLDivElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  // Function to get initials from name
  const getInitials = (name?: string): string => {
    if (!name) return '';
    const nameParts = name.split(' ');
    if (nameParts.length === 1) return nameParts[0].substring(0, 2).toUpperCase();
    return (nameParts[0][0] + (nameParts[nameParts.length - 1][0] || '')).toUpperCase();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }

      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setIsCountryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const userInitials = userInfoProfile ? getInitials(userInfoProfile.name) : '';

  const handleLogout = () => {
    setIsDropdownOpen(false);
    try {
      logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if there's an error
      window.location.href = '/login';
    }
  };

  const handleCountrySelect = (country: { code: string; name: string }) => {
    setIsCountryDropdownOpen(false);
    void changeLocale(country.code);
  };

  const rightContent: React.ReactElement | undefined = isAuthenticated ? (
    <>
      <div className='selecCountries' ref={countryDropdownRef}>
        <div className="relative">
          <button
            onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50"
            aria-haspopup="true"
            aria-expanded={isCountryDropdownOpen}
            disabled={!hasMultipleLocales}
          >
            <div className='w-5 h-4'>
              <Icon name={`countries/${countryCode}` as any} />
            </div>
            <span className="text-sm font-medium text-gray-700">{countryName}</span>
            <div className={`w-4 h-4 text-gray-500 transition-transform ${isCountryDropdownOpen ? 'rotate-180' : ''} ${!hasMultipleLocales ? 'hidden' : ''}`}>
              <Icon name="arrow/chevron-down" />
            </div>
          </button>

          {hasMultipleLocales && isCountryDropdownOpen && (
            <div className="absolute right-0 mt-2 w-60 bg-white rounded-md shadow-xl z-50 border border-gray-200">
              <div className="py-1">
                {locales.map((country) => (
                  <button
                    key={country.code}
                    onClick={() => handleCountrySelect(country)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${countryCode === country.code ? 'bg-yellow-50 text-yellow-700' : 'text-gray-700'
                      }`}
                  >
                    <div className='w-5 h-4'>
                      <Icon name={`countries/${country.code}` as any} />
                    </div>
                    <span className="font-medium">{country.name}</span>
                    {countryCode === country.code && (
                      <div className="w-4 h-4 text-yellow-600 ml-auto">
                        <Icon name="validation/check" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="w-10 h-10 bg-black rounded-full flex items-center justify-center text-white font-bold text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300 hover:bg-black transition-colors"
          aria-haspopup="true"
          aria-expanded={isDropdownOpen}
          title={`${userInfoProfile?.name || 'Usuario'} - ${userInfoProfile?.email || ''}`}
        >
          {userInitials || 'U'}
        </button>

        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-xl z-50 border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <p className="text-sm font-semibold text-gray-800 truncate" title={userInfoProfile?.name || 'Usuario'}>
                {userInfoProfile?.name || 'Usuario'}
              </p>

              <p className="text-xs text-gray-500 truncate" title={userInfoProfile?.email || ''}>
                {userInfoProfile?.email || ''}
              </p>
            </div>

            <div className="p-2">
              <Button
                variant="secondary"
                onClick={handleLogout}
                className="w-full flex items-center justify-start text-gray-700 hover:bg-gray-100 px-3 py-2 rounded-md text-sm transition-colors"
              >
                Cerrar Sesión
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  ) : undefined;

  return (
    <TopNav
      className="z-20 w-full bg-white shadow-md"
      logoSrc={logo}
      logoAlt="Logo SmartComms"
      right={rightContent}
      sideNavControl={isAuthenticated} // Only show side nav control when authenticated
    />
  );
};

export default Header;